import React, {useEffect, useRef, useState} from 'react'
import {useParams, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux';
import MarkDownContent from '../../elements/MarkDownContent';
import { BRAND_NAME } from '../../config/branding';
import {toast} from 'react-toastify';
import {Button, Grid} from '@mui/material';
import {styled} from "@mui/material/styles";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import {displayAddess, formatCurrency, isJsonString, uploadToS3} from "../../utils/globalFunctions";
import ReadOnly from "../../elements/Editor/readOnly";
import SkillChips from "../../elements/SkillChip/SkillChipV3";
import {createJobApplication, fetchJobById} from '../../services/job.service';
import * as actions from "../../store/actions";
import {CreateButton} from "../../elements/CreateButton";
import {DEFAULT_COMPANY_TABLE_URL, JOB_REGION} from '../../utils/constant';
import {FlexRowWrapper} from "../../elements/Row";
import Spin from '../../elements/Loader/spin';
import MainCard from '../../elements/MainCard';
import PerkCard from '../businesses/perkCard';
import JobApplicationModal from '../Modals/jobApplicationModal';
import { getCandidatesRole } from '../../store/actions/candidate';

import '../../elements/Editor/styles.scss'
import './styles.scss';

dayjs.extend(relativeTime)

export const EditButton = styled(Button)(() => ({
  minWidth: '44px',
  color: '#FFFFFF',
  backgroundColor: '#283665 !important',
  '&:hover': {
    backgroundColor: '#283665',
  },
}));

const CustomGrid = styled(Grid)(() => ({
  marginBottom: '0.5rem'
}));

const StyledImg = styled('img')`
  width: 70px !important;
  height: 70px !important;
`

export const BackButton = styled(Button)(() => ({
  minWidth: '44px',
  color: '#FFFFFF',
  backgroundColor: '#6c757d !important',
  '&:hover': {
    backgroundColor: '#6c757d',
  },
}));

const LogoSpan = styled("span")`
   width: 100px;
   height: 100px;
   background-size: contain;
   background-repeat: no-repeat;
   border-radius:10px;
   border: 1px solid #f0f0f0;
`;

const JobDetail = ({ ...props }) => {
  const jobDetailRef = useRef();

  const { jobId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [job, setJob] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isApplyingLoading, setIsApplyingLoading] = useState(false);
  const [openJobApplicationModal, setOpenJobApplicationModal] = useState(false);

  const appliedJobs = localStorage.getItem(`appliedJobs`) ? JSON.parse(localStorage.getItem(`appliedJobs`)) : [];

  const { candidatesRole, user, userAuthenticated } = useSelector((state) => {
    return {
      candidatesRole: state.candidates?.candidatesRole,
      user: state?.auth?.user,
      userAuthenticated: state?.auth?.accessToken
    }
  });

  useEffect(() => {
    document.body.classList.add('job-page-body');
    
    return () => {
      document.body.classList.remove('job-page-body');
    };
  }, []);

  useEffect(() => {
    if (!props?.skills?.length) {
      dispatch(actions.getSkills());
    }
    if (!candidatesRole?.length) {
      dispatch(getCandidatesRole());
    }
  }, [props?.skills, candidatesRole]);


  const handleJobClick = () => {
    setOpenJobApplicationModal(true);
  }

  const handleJobApplicationModalClose = () => {
    setOpenJobApplicationModal(false);
  }

  const handleSubmitJobApplicationData = async (jobApplicationData) => {
    const appliedJobData = localStorage.getItem(`appliedJobData`) ? JSON.parse(localStorage.getItem(`appliedJobData`)) : {};

    if (appliedJobData?.email === jobApplicationData?.candidateEmail && (appliedJobs || [])?.includes(jobId)) {
      handleJobApplicationModalClose();
      toast.success(`Your application has been submitted successfully.`);
      if (job?.originalJobPageUrl) {
        window.open(job?.originalJobPageUrl, '_blank');
      }
      return;
    }

    setIsApplyingLoading(true);
    const requestBody = {
      jobId,
      firstName: jobApplicationData?.firstName,
      lastName: jobApplicationData?.lastName,
      email: jobApplicationData?.candidateEmail,
      mobile: {
        phone: jobApplicationData?.phone || "",
        phoneCode: jobApplicationData?.phoneCode || ""
      },
      country: jobApplicationData?.country,
      state: jobApplicationData?.state,
      ...(userAuthenticated ? {candidateId: user?.candidateId} : {})
    }

    let resume = typeof jobApplicationData?.resume === "string" ? jobApplicationData?.resume?.replace(process.env.REACT_APP_PUBLIC_CDN_URL, "") : ""
    if (jobApplicationData?.resume instanceof File) {
      await uploadToS3(jobApplicationData?.resume, "jobApplicationResume", `${jobId}_${dayjs().unix()}`).then((url) => {
        resume = url.replace(process.env.REACT_APP_PUBLIC_CDN_URL, "");
      })
    }

    requestBody.resume = resume;
    await createJobApplication(jobId, requestBody).then(response => {
      setIsApplyingLoading(false);
      if (response?.statusCode >= 200 && response?.statusCode < 400) {
        handleJobApplicationModalClose();
        toast.success(`Your application has been submitted successfully.`);
        localStorage.setItem(`appliedJobData`, JSON.stringify(requestBody));
        appliedJobs.push(jobId)
        localStorage.setItem(`appliedJobs`, JSON.stringify(appliedJobs));
        if (job?.originalJobPageUrl) {
          window.open(job?.originalJobPageUrl, '_blank');
        }
      } else {
        toast.error(response.message || `Error while submitting job application, please try again`);
      }
    }).catch(error => {
      setIsApplyingLoading(false);
      const errorMessage = JSON.parse(error?.response);
      toast.error(errorMessage?.message || `Error while submitting job application, please try again`);
    });
  }

  const fetchJobDetails = () => {
    setIsLoading(true);
    fetchJobById(jobId, `?userId=${user?._id || ''}`).then(async (response) => {
      setIsLoading(false);
      if (response?.data?.job) {
        setJob(response?.data?.job);
      }
    }).catch((error) => {
      setIsLoading(false);
      toast.error(error.message);
      navigate(`/jobs`);
    })
  }

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId, user?.candidateId]);

  const renderStatus = (status) => {
    switch (status) {
      case 'applied':
        return "Applied"
      case 'rejected':
        return "Rejected"
      case 'accepted':
        return "Accepted"
      case 'interview_scheduled':
        return "Interview Scheduled"
      case 'selected':
        return "Selected"
      case 'closed':
        return "Closed"
      default:
        return ""
    }
  }

  const isJsonSummary = isJsonString(job?.summary);
  const isJsonBusinessSummary = isJsonString(job?.businessId?.summary);
  let opportunitySteps = job?.opportunityTimeline?.length ? job?.opportunityTimeline : [];
  opportunitySteps = opportunitySteps.map(step => renderStatus(step?.toStatus));
  const allStatus = [...opportunitySteps];
  const lastStatus = allStatus.pop();
  if (lastStatus !== 'Rejected' && lastStatus !== 'Closed' && lastStatus !== 'Selected') {
    opportunitySteps.push("All steps");
  }

  const selectedRole = (candidatesRole || [])?.find(status => status?.slug === job?.role);
  const selectedRegion = (JOB_REGION || [])?.find(region => region?.value === job?.region);

  const budgetType = job?.budget?.budgetType?.trim() === "monthly"
    ? 'per month'
    : job?.budget?.budgetType?.trim() === "annual"
    ? "per year"
    : job?.budget?.budgetType?.trim() === "hourly"
    ? "per hour"
    : job?.budget?.budgetType?.trim() === "budgetNotMentioned"
    ? "not mentioned"
    : "per year";

  const isCandidateApplied = !userAuthenticated && (appliedJobs || [])?.includes(jobId) ? true : !!job?.isCandidateApplied;
  return (
    <div className={"print-body-wrapper job-detail-container"} ref={(me) => jobDetailRef.current = me}>
      <div style={{display: "none"}} className={"print-background-div"}/>
        <Spin isLoading={isLoading}>
          <div className="icon-left-side-with-text">
            <div className='row-left-side'>
              <LogoSpan className={`image-span`} style={{ backgroundImage: `url(${job?.businessId?.logo || DEFAULT_COMPANY_TABLE_URL})` }}>
              </LogoSpan>
              <span className='row-right-side'>
                <FlexRowWrapper>
                  <h1>
                    {job?.title ? job?.title : selectedRole?.title ? selectedRole?.title : "Job detail"}
                  </h1>
                </FlexRowWrapper>
              </span>
            </div>
            <CreateButton
              size="small"
              className="print-hide"
              variant="contained"
              style={{ float: 'right' }}
              onClick={handleJobClick}
              disabled={!!isCandidateApplied}
            >
              { !!isCandidateApplied ? "Applied" : "Apply Now" }
            </CreateButton>
            <BackButton size="small" variant="contained" style={{ float: 'right', marginRight: '10px' }} onClick={() => navigate('/jobs')}>
              <ArrowBackIosNewIcon sx={'md'}/>Back to Jobs
            </BackButton>
          </div>
          <div className="wrapper">
            <div className="main-container print-page-break">
              <div className="searched-jobs">
                <div className="job-overview print-page-break">
                  <div className="job-explain">
                    <div className="job-explain-content">
                      <div className="job-subtitle-wrapper">
                        <div className={`company-name`}>
                          <a href={`${job?.businessId?.website}`} className={`cursor-pointer`} target='_blank' rel="noopener noreferrer">{job?.businessId?.name || BRAND_NAME}</a>
                          &nbsp;
                          {
                            job?.businessId?.location?.city || job?.businessId?.location?.state || job?.businessId?.location?.country?.name ?
                              <span className={`comp-location`}>
                                {displayAddess(job?.businessId?.location?.city, job?.businessId?.location?.state, job?.businessId?.location?.country?.name)}
                              </span>
                            : null
                          }
                        </div>
                        <div className="posted print-hide">{dayjs(job?.createdAt).fromNow()}</div>
                      </div>
                      <div className={`overview-text-subheader-summary`}>
                        {
                          isJsonBusinessSummary ?
                            <ReadOnly initialValue={JSON.parse(job?.businessId?.summary) || []}/>
                          : <MarkDownContent className="slate-editor">{job?.businessId?.summary}</MarkDownContent>
                        }
                      </div>
                      <div className="overview-text">
                        <div className="overview-text-header">Job Overview</div>
                      </div>
                      <div className="explain-bar">
                        <div className="explain-contents">
                          <div className="explain-title">Experience</div>
                          <div className="explain-subtitle">
                            {
                              job?.expNeeded?.min === job?.expNeeded?.max ?
                                `${job?.expNeeded?.min} Years`
                              : `${job?.expNeeded?.min} to ${job?.expNeeded?.max} Years`
                            }
                          </div>
                        </div>
                        {
                          job?.timezone ?
                            <div className={`explain-contents`}>
                              <div className="explain-title">Timezone</div>
                              <div className="explain-subtitle">{job?.timezone}</div>
                            </div>
                          : null
                        }
                        {
                          job?.region ?
                              <div className="explain-contents">
                                <div className="explain-title">Region</div>
                                <div className="explain-subtitle">{selectedRegion?.label}</div>
                              </div>
                              : null
                        }
                        <div className="explain-contents">
                          <div className="explain-title">Overnight working allowed?</div>
                          <div className="explain-subtitle">{job?.isOverNightWorkingAllowed ? "Yes" : "No"}</div>
                        </div>
                        {
                          job?.overlappingNeededHours ?
                            <div className={`explain-contents`}>
                              <div className="explain-title">Overlapping Hours</div>
                              <div className="explain-subtitle">{job?.overlappingNeededHours}</div>
                            </div>
                          : null
                        }

                        <div className={`explain-contents`}>
                          <div className="explain-title">Contract duration</div>
                          <div className="explain-subtitle">
                          {
                              job?.contractDuration?.min === job?.contractDuration?.max ?
                              `${job?.contractDuration?.min} Months`
                              : `${job?.contractDuration?.min} to ${job?.contractDuration?.max} Months`
                            }

                          </div>
                        </div>
                        <div className={`explain-contents`}>
                          <div className="explain-title">Offer Salary</div>
                          <div className="explain-subtitle">{formatCurrency(job?.budget?.min, job?.budget?.currency)} to {formatCurrency(job?.budget?.max, job?.budget?.currency)}&nbsp;{budgetType}</div>
                        </div>
                      </div>
                      <div className="overview-text">
                        <div className="overview-text-subheader">
                          {
                            isJsonSummary ?
                              <ReadOnly initialValue={JSON.parse(job?.summary) || []}/>
                            : <MarkDownContent className="slate-editor">{job?.summary}</MarkDownContent>
                          }
                        </div>
                      </div>
                      <div className="overview-text">
                        <div className="overview-text-header">Must have skills</div>
                        <FlexRowWrapper>
                          {
                            job?.skills?.primarySkills?.map(skill => {
                              return (
                                  <SkillChips skillName={skill}/>
                              )
                            })
                          }
                        </FlexRowWrapper>
                      </div>
                      {job?.skills?.secondarySkills?.length ?
                          <div className="overview-text">
                            <div className="overview-text-header">Good to have skills</div>
                            <FlexRowWrapper>
                              {
                                job?.skills?.secondarySkills?.map(skill => {
                                  return (
                                      <SkillChips skillName={skill}/>
                                  )
                                })
                              }
                            </FlexRowWrapper>
                          </div> : null}
                      <div className="overview-text">
                        <div className="overview-text-header">Job Description</div>
                        <div className="overview-text-item">
                          <MarkDownContent className="slate-editor">
                            {job?.responsibility}
                          </MarkDownContent>
                        </div>
                      </div>
                      <div className="overview-text">
                        <div className="overview-text-header">Benefits & Perks</div>
                        <div className="overview-text-item">
                          <MainCard style={{ border: 'none', padding: '0px' }} className="job-perk-cards print-no-margin">
                            <Grid container spacing={2}>
                              {
                                job?.businessId?.perks?.map((perkItem) => {
                                  return (
                                    <CustomGrid item xs={6} md={6} className='print-page-break print-page-break-avoid'>
                                      <PerkCard perkItem={perkItem} isSelected={true} cardMode="jobDetail" />
                                    </CustomGrid>
                                  )
                                })
                              }
                            </Grid>
                          </MainCard>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {
              openJobApplicationModal ?
                <JobApplicationModal
                  isOpen={openJobApplicationModal}
                  handleClose={handleJobApplicationModalClose}
                  handleSubmit={handleSubmitJobApplicationData}
                  jobId={jobId}
                  isApplyingLoading={isApplyingLoading}
                />
              : null
            }
          </div>
        </Spin>
    </div>
  )
}

export default JobDetail;
